import { Slot } from "@radix-ui/react-slot";
import { type VariantProps, cva } from "class-variance-authority";
import * as React from "react";

import { cn } from "@sunfish/lib/utils";
import { ArrowRightIcon, LoaderCircleIcon } from "lucide-react";

const buttonVariants = cva(
  "inline-flex items-end justify-start gap-2 whitespace-nowrap rounded-xl text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none text-3xl font-light w-full border",
  {
    variants: {
      variant: {
        default:
          "bg-brand-500 text-brand-foreground shadow hover:bg-brand-500/90 [&_svg]:text-brand-500",
        destructive:
          "bg-destructive text-destructive-foreground shadow-sm hover:bg-destructive/90",
        secondary:
          "bg-blue text-blue-foreground shadow-sm hover:bg-blue/80 [&_svg]:text-blue dark:bg-blue-600 dark:border-bg-blue",
      },
      size: {
        default: "h-56 p-6",
        sm: "h-24 rounded-xl p-3 text-2xl",
        lg: "h-96 rounded-xl p-8",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  isLoading?: boolean;
}

const CtaButton = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, isLoading, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button";
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        {...props}
      >
        <div className="w-full">
          <div className="w-full flex items-center justify-between gap-14">
            <span className="flex-grow text-start">{props.children}</span>
            <span className="p-2 px-6 bg-white rounded-xl ">
              {isLoading ? (
                <LoaderCircleIcon className="animate-spin" />
              ) : (
                <ArrowRightIcon className="size-6" />
              )}
            </span>
          </div>
        </div>
      </Comp>
    );
  },
);
CtaButton.displayName = "Button";

export { CtaButton, buttonVariants };
